import { useState } from "react";
import { Code, Copy, Check, ChevronDown, ChevronUp } from "lucide-react";

const TABS = [
  { id: "python", label: "Python" },
  { id: "javascript", label: "JavaScript / Node.js" },
  { id: "php", label: "PHP" },
  { id: "csharp", label: "C# / .NET" },
];

const hmacPython = `import hmac
import hashlib
import json
import base64
from datetime import datetime

def verify_license(license_key: str, secret_key: str, prefix: str = "CF") -> dict:
    """
    Verifie une licence HMAC-SHA256 (ClaimFlow / Generic).
    Format: PREFIX-{payload_b64}.{signature_24}
    
    Args:
        license_key: La cle de licence a verifier
        secret_key: La cle secrete du generateur (meme que dans SCIAD Admin)
        prefix: Le prefixe attendu (CF, PH, ou votre prefixe custom)
    
    Returns:
        dict avec 'valid', 'client_code', 'max_users', 'expires_at', 'expired'
    """
    try:
        # 1. Separer le prefixe
        if not license_key.startswith(prefix + "-"):
            return {"valid": False, "error": "Prefixe invalide"}
        
        body = license_key[len(prefix) + 1:]  # Tout apres "CF-"
        
        # 2. Separer payload et signature
        if "." not in body:
            return {"valid": False, "error": "Format invalide"}
        
        payload_b64, signature = body.rsplit(".", 1)
        
        # 3. Verifier la signature HMAC-SHA256
        expected_sig = hmac.new(
            secret_key.encode(),
            payload_b64.encode(),
            hashlib.sha256
        ).hexdigest()[:24]
        
        if not hmac.compare_digest(signature, expected_sig):
            return {"valid": False, "error": "Signature invalide"}
        
        # 4. Decoder le payload (base64url)
        padding = 4 - len(payload_b64) % 4
        if padding != 4:
            payload_b64 += "=" * padding
        
        payload_json = base64.urlsafe_b64decode(payload_b64).decode()
        data = json.loads(payload_json)
        
        # 5. Verifier l'expiration
        expired = False
        if data.get("e"):
            exp_date = datetime.strptime(data["e"], "%Y-%m-%d")
            expired = datetime.now() > exp_date
        
        return {
            "valid": True,
            "client_code": data.get("c", ""),
            "max_users": data.get("u", 0),
            "expires_at": data.get("e", ""),
            "expired": expired,
            "version": data.get("v", 1),
        }
    
    except Exception as e:
        return {"valid": False, "error": str(e)}


# ===== UTILISATION =====
SECRET_KEY = "VOTRE_CLE_SECRETE"  # Meme cle que dans SCIAD Admin > Generateurs
LICENSE = "CF-eyJjIjoiQ0xJRU5UIiwidSI6NSwiZSI6IjIwMjYtMTItMzEiLCJ2IjoxfQ.abc123..."

result = verify_license(LICENSE, SECRET_KEY)

if result["valid"] and not result["expired"]:
    print(f"Licence valide pour {result['max_users']} utilisateurs")
    print(f"Client: {result['client_code']}")
    print(f"Expire le: {result['expires_at']}")
else:
    print(f"Licence invalide: {result.get('error', 'expiree')}")
`;

const hmacJS = `const crypto = require('crypto');

/**
 * Verifie une licence HMAC-SHA256 (ClaimFlow / Generic).
 * Format: PREFIX-{payload_b64}.{signature_24}
 * 
 * @param {string} licenseKey - La cle de licence
 * @param {string} secretKey - La cle secrete du generateur
 * @param {string} prefix - Le prefixe attendu (CF, PH, ou custom)
 * @returns {object} { valid, clientCode, maxUsers, expiresAt, expired }
 */
function verifyLicense(licenseKey, secretKey, prefix = 'CF') {
  try {
    // 1. Verifier le prefixe
    if (!licenseKey.startsWith(prefix + '-')) {
      return { valid: false, error: 'Prefixe invalide' };
    }

    const body = licenseKey.slice(prefix.length + 1);

    // 2. Separer payload et signature
    const dotIndex = body.lastIndexOf('.');
    if (dotIndex === -1) return { valid: false, error: 'Format invalide' };

    const payloadB64 = body.slice(0, dotIndex);
    const signature = body.slice(dotIndex + 1);

    // 3. Verifier la signature HMAC-SHA256
    const expectedSig = crypto
      .createHmac('sha256', secretKey)
      .update(payloadB64)
      .digest('hex')
      .slice(0, 24);

    if (signature !== expectedSig) {
      return { valid: false, error: 'Signature invalide' };
    }

    // 4. Decoder le payload (base64url -> JSON)
    let padded = payloadB64;
    while (padded.length % 4 !== 0) padded += '=';
    const payloadJson = Buffer.from(padded, 'base64url').toString('utf8');
    const data = JSON.parse(payloadJson);

    // 5. Verifier l'expiration
    const expired = data.e ? new Date() > new Date(data.e) : false;

    return {
      valid: true,
      clientCode: data.c || '',
      maxUsers: data.u || 0,
      expiresAt: data.e || '',
      expired,
      version: data.v || 1,
    };
  } catch (e) {
    return { valid: false, error: e.message };
  }
}


// ===== UTILISATION =====
const SECRET_KEY = 'VOTRE_CLE_SECRETE'; // Meme cle que dans SCIAD Admin
const LICENSE = 'CF-eyJjIjoiQ0xJRU5UIiwidSI6NSwiZSI6IjIwMjYtMTItMzEiLCJ2IjoxfQ.abc123...';

const result = verifyLicense(LICENSE, SECRET_KEY);

if (result.valid && !result.expired) {
  console.log(\`Licence valide pour \${result.maxUsers} utilisateurs\`);
  console.log(\`Client: \${result.clientCode}\`);
  console.log(\`Expire le: \${result.expiresAt}\`);
} else {
  console.log(\`Licence invalide: \${result.error || 'expiree'}\`);
}
`;

const hmacPHP = `<?php
/**
 * Verifie une licence HMAC-SHA256 (ClaimFlow / Generic).
 * Format: PREFIX-{payload_b64}.{signature_24}
 *
 * @param string $licenseKey La cle de licence
 * @param string $secretKey La cle secrete du generateur
 * @param string $prefix Le prefixe attendu (CF, PH, ou custom)
 * @return array ['valid' => bool, 'client_code' => '', 'max_users' => 0, ...]
 */
function verifyLicense(string $licenseKey, string $secretKey, string $prefix = 'CF'): array {
    // 1. Verifier le prefixe
    if (strpos($licenseKey, $prefix . '-') !== 0) {
        return ['valid' => false, 'error' => 'Prefixe invalide'];
    }

    $body = substr($licenseKey, strlen($prefix) + 1);

    // 2. Separer payload et signature
    $dotPos = strrpos($body, '.');
    if ($dotPos === false) {
        return ['valid' => false, 'error' => 'Format invalide'];
    }

    $payloadB64 = substr($body, 0, $dotPos);
    $signature  = substr($body, $dotPos + 1);

    // 3. Verifier la signature HMAC-SHA256
    $expectedSig = substr(
        hash_hmac('sha256', $payloadB64, $secretKey),
        0, 24
    );

    if (!hash_equals($expectedSig, $signature)) {
        return ['valid' => false, 'error' => 'Signature invalide'];
    }

    // 4. Decoder le payload (base64url -> JSON)
    $padded = str_pad($payloadB64, strlen($payloadB64) + (4 - strlen($payloadB64) % 4) % 4, '=');
    $payloadJson = base64_decode(strtr($padded, '-_', '+/'));
    $data = json_decode($payloadJson, true);

    if (!$data) {
        return ['valid' => false, 'error' => 'Payload invalide'];
    }

    // 5. Verifier l'expiration
    $expired = false;
    if (!empty($data['e'])) {
        $expired = time() > strtotime($data['e']);
    }

    return [
        'valid'       => true,
        'client_code' => $data['c'] ?? '',
        'max_users'   => $data['u'] ?? 0,
        'expires_at'  => $data['e'] ?? '',
        'expired'     => $expired,
        'version'     => $data['v'] ?? 1,
    ];
}


// ===== UTILISATION =====
$SECRET_KEY = 'VOTRE_CLE_SECRETE'; // Meme cle que dans SCIAD Admin
$LICENSE    = 'CF-eyJjIjoiQ0xJRU50...';

$result = verifyLicense($LICENSE, $SECRET_KEY);

if ($result['valid'] && !$result['expired']) {
    echo "Licence valide pour {$result['max_users']} utilisateurs\\n";
    echo "Client: {$result['client_code']}\\n";
    echo "Expire le: {$result['expires_at']}\\n";
} else {
    echo "Licence invalide: " . ($result['error'] ?? 'expiree') . "\\n";
}
?>
`;

const hmacCSharp = `using System;
using System.Security.Cryptography;
using System.Text;
using System.Text.Json;

public class LicenseResult {
    public bool Valid { get; set; }
    public string Error { get; set; }
    public string ClientCode { get; set; }
    public int MaxUsers { get; set; }
    public string ExpiresAt { get; set; }
    public bool Expired { get; set; }
    public int Version { get; set; }
}

public static class LicenseVerifier
{
    /// <summary>
    /// Verifie une licence HMAC-SHA256 (ClaimFlow / Generic).
    /// Format: PREFIX-{payload_b64}.{signature_24}
    /// </summary>
    public static LicenseResult Verify(string licenseKey, string secretKey, string prefix = "CF")
    {
        try
        {
            // 1. Verifier le prefixe
            if (!licenseKey.StartsWith(prefix + "-"))
                return new LicenseResult { Valid = false, Error = "Prefixe invalide" };

            string body = licenseKey.Substring(prefix.Length + 1);

            // 2. Separer payload et signature
            int dotIndex = body.LastIndexOf('.');
            if (dotIndex < 0)
                return new LicenseResult { Valid = false, Error = "Format invalide" };

            string payloadB64 = body.Substring(0, dotIndex);
            string signature  = body.Substring(dotIndex + 1);

            // 3. Verifier la signature HMAC-SHA256
            using var hmac = new HMACSHA256(Encoding.UTF8.GetBytes(secretKey));
            byte[] hash = hmac.ComputeHash(Encoding.UTF8.GetBytes(payloadB64));
            string expectedSig = BitConverter.ToString(hash)
                .Replace("-", "").ToLower().Substring(0, 24);

            if (signature != expectedSig)
                return new LicenseResult { Valid = false, Error = "Signature invalide" };

            // 4. Decoder le payload (base64url -> JSON)
            string padded = payloadB64;
            while (padded.Length % 4 != 0) padded += "=";
            padded = padded.Replace('-', '+').Replace('_', '/');
            string json = Encoding.UTF8.GetString(Convert.FromBase64String(padded));

            using var doc = JsonDocument.Parse(json);
            var root = doc.RootElement;

            string clientCode = root.TryGetProperty("c", out var c) ? c.GetString() : "";
            int maxUsers = root.TryGetProperty("u", out var u) ? u.GetInt32() : 0;
            string expiresAt = root.TryGetProperty("e", out var e) ? e.GetString() : "";
            int version = root.TryGetProperty("v", out var v) ? v.GetInt32() : 1;

            // 5. Verifier l'expiration
            bool expired = false;
            if (!string.IsNullOrEmpty(expiresAt))
                expired = DateTime.Now > DateTime.Parse(expiresAt);

            return new LicenseResult {
                Valid = true, ClientCode = clientCode, MaxUsers = maxUsers,
                ExpiresAt = expiresAt, Expired = expired, Version = version
            };
        }
        catch (Exception ex)
        {
            return new LicenseResult { Valid = false, Error = ex.Message };
        }
    }
}


// ===== UTILISATION =====
var SECRET_KEY = "VOTRE_CLE_SECRETE"; // Meme cle que dans SCIAD Admin
var LICENSE = "CF-eyJjIjoiQ0xJRU50...";

var result = LicenseVerifier.Verify(LICENSE, SECRET_KEY);

if (result.Valid && !result.Expired) {
    Console.WriteLine($"Licence valide pour {result.MaxUsers} utilisateurs");
    Console.WriteLine($"Client: {result.ClientCode}");
    Console.WriteLine($"Expire le: {result.ExpiresAt}");
} else {
    Console.WriteLine($"Licence invalide: {result.Error ?? "expiree"}");
}
`;

const simpleHashPython = `def verify_projethub_key(license_key: str, prefix: str = "PH") -> dict:
    """
    Decode une licence ProjetHub (simple hash).
    Format: PH-{T}{NNN}-{XXXX}-{XXXX}-{XXXX}
    
    T = Type de licence (S=Standard, P=Premium, E=Enterprise)
    NNN = Nombre max d'utilisateurs (ex: 005, 050, 999)
    
    Note: Ce format ne contient PAS de signature cryptographique.
    La verification se fait par lookup en base de donnees ou API.
    """
    try:
        if not license_key.startswith(prefix + "-"):
            return {"valid": False, "error": "Prefixe invalide"}
        
        parts = license_key.split("-")
        if len(parts) != 5:
            return {"valid": False, "error": "Format invalide (5 segments attendus)"}
        
        type_users = parts[1]  # Ex: "S005"
        license_type = {"S": "Standard", "P": "Premium", "E": "Enterprise"}.get(type_users[0], "Inconnu")
        max_users = int(type_users[1:])
        
        return {
            "valid": True,
            "license_type": license_type,
            "max_users": max_users,
            "hash_segments": f"{parts[2]}-{parts[3]}-{parts[4]}",
        }
    except Exception as e:
        return {"valid": False, "error": str(e)}


# ===== UTILISATION =====
LICENSE = "PH-S005-A1B2-C3D4-E5F6"
result = verify_projethub_key(LICENSE)
print(result)
# {'valid': True, 'license_type': 'Standard', 'max_users': 5, 'hash_segments': 'A1B2-C3D4-E5F6'}
`;

const simpleHashJS = `/**
 * Decode une licence ProjetHub (simple hash).
 * Format: PH-{T}{NNN}-{XXXX}-{XXXX}-{XXXX}
 */
function verifyProjetHubKey(licenseKey, prefix = 'PH') {
  try {
    if (!licenseKey.startsWith(prefix + '-'))
      return { valid: false, error: 'Prefixe invalide' };

    const parts = licenseKey.split('-');
    if (parts.length !== 5)
      return { valid: false, error: 'Format invalide (5 segments attendus)' };

    const typeUsers = parts[1]; // Ex: "S005"
    const types = { S: 'Standard', P: 'Premium', E: 'Enterprise' };
    const licenseType = types[typeUsers[0]] || 'Inconnu';
    const maxUsers = parseInt(typeUsers.slice(1), 10);

    return {
      valid: true,
      licenseType,
      maxUsers,
      hashSegments: parts.slice(2).join('-'),
    };
  } catch (e) {
    return { valid: false, error: e.message };
  }
}

// ===== UTILISATION =====
const result = verifyProjetHubKey('PH-S005-A1B2-C3D4-E5F6');
console.log(result);
`;

const CodeBlock = ({ code, lang }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group">
      <button onClick={copy} className="absolute top-3 right-3 p-2 rounded-lg bg-white/10 hover:bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity z-10" data-testid={`copy-${lang}`}>
        {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-slate-400" />}
      </button>
      <pre className="p-4 rounded-xl bg-[#0B1121] border border-white/10 overflow-x-auto text-sm text-slate-300 leading-relaxed max-h-[500px] overflow-y-auto">
        <code>{code}</code>
      </pre>
    </div>
  );
};

const LicenseIntegrationGuide = () => {
  const [activeTab, setActiveTab] = useState("python");
  const [showHMAC, setShowHMAC] = useState(true);
  const [showSimple, setShowSimple] = useState(false);

  const hmacCode = { python: hmacPython, javascript: hmacJS, php: hmacPHP, csharp: hmacCSharp };
  const simpleCode = { python: simpleHashPython, javascript: simpleHashJS, php: "// Meme logique que JavaScript — split('-') et lecture du type/users", csharp: "// Meme logique — Split('-') et lecture du type/users" };

  return (
    <div className="space-y-6" data-testid="license-integration-guide">
      {/* HMAC-SHA256 Section */}
      <div className="rounded-2xl bg-[#131B2F] border border-white/10 overflow-hidden">
        <button onClick={() => setShowHMAC(!showHMAC)} className="w-full flex items-center justify-between p-5 hover:bg-white/5 transition-colors" data-testid="toggle-hmac-section">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#2395E3]/20"><Code className="w-5 h-5 text-[#2395E3]" /></div>
            <div className="text-left">
              <h3 className="font-semibold">HMAC-SHA256 (ClaimFlow / Generic)</h3>
              <p className="text-xs text-slate-400 mt-0.5">Verification cryptographique — pour vos applications securisees</p>
            </div>
          </div>
          {showHMAC ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
        </button>

        {showHMAC && (
          <div className="px-5 pb-5 space-y-4">
            {/* Format explanation */}
            <div className="p-4 rounded-xl bg-[#0B1121] border border-[#2395E3]/20 space-y-3 text-sm">
              <p className="font-semibold text-[#2395E3]">Format de la cle</p>
              <code className="block text-[#00CCC2] text-xs bg-[#131B2F] p-3 rounded-lg">
                PREFIX-{"{"} payload_base64url {"}"}.{"{"} signature_hmac_24_chars {"}"}
              </code>
              <div className="space-y-2 text-slate-400">
                <p><strong className="text-white">PREFIX</strong> : Identifiant du generateur (CF, PH, ou votre prefixe custom)</p>
                <p><strong className="text-white">payload</strong> : JSON encode en base64url contenant :</p>
                <ul className="ml-4 space-y-1">
                  <li><code className="text-[#00CCC2]">c</code> = code client (ex: "MONENTREPRISE")</li>
                  <li><code className="text-[#00CCC2]">u</code> = nombre max d'utilisateurs (ex: 5)</li>
                  <li><code className="text-[#00CCC2]">e</code> = date d'expiration "YYYY-MM-DD" (ex: "2026-12-31")</li>
                  <li><code className="text-[#00CCC2]">v</code> = version du format (1)</li>
                </ul>
                <p><strong className="text-white">signature</strong> : 24 premiers caracteres du HMAC-SHA256(secret, payload_b64)</p>
              </div>
            </div>

            {/* Algo steps */}
            <div className="p-4 rounded-xl bg-[#0B1121] border border-white/10 text-sm space-y-2">
              <p className="font-semibold text-white">Algorithme de verification (5 etapes)</p>
              <ol className="space-y-1 text-slate-400 list-decimal ml-4">
                <li>Verifier que la cle commence par le bon <strong>PREFIX-</strong></li>
                <li>Separer le <strong>payload_b64</strong> et la <strong>signature</strong> au dernier point</li>
                <li>Calculer <code className="text-[#00CCC2]">HMAC-SHA256(secret_key, payload_b64)</code> et comparer les 24 premiers hex</li>
                <li>Decoder le payload : <code className="text-[#00CCC2]">base64url_decode(payload_b64) → JSON</code></li>
                <li>Verifier la date d'expiration</li>
              </ol>
            </div>

            {/* Language tabs */}
            <div className="flex gap-2 border-b border-white/10 pb-2">
              {TABS.map((tab) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
                  className={`px-4 py-2 text-sm rounded-lg transition-colors ${activeTab === tab.id ? "bg-[#2395E3]/20 text-[#2395E3]" : "text-slate-400 hover:bg-white/5"}`}>
                  {tab.label}
                </button>
              ))}
            </div>

            <CodeBlock code={hmacCode[activeTab] || hmacCode.python} lang={`hmac-${activeTab}`} />
          </div>
        )}
      </div>

      {/* Simple Hash Section */}
      <div className="rounded-2xl bg-[#131B2F] border border-white/10 overflow-hidden">
        <button onClick={() => setShowSimple(!showSimple)} className="w-full flex items-center justify-between p-5 hover:bg-white/5 transition-colors" data-testid="toggle-simple-section">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#00CCC2]/20"><Code className="w-5 h-5 text-[#00CCC2]" /></div>
            <div className="text-left">
              <h3 className="font-semibold">Simple Hash (ProjetHub)</h3>
              <p className="text-xs text-slate-400 mt-0.5">Format lisible — type et nombre d'utilisateurs dans la cle</p>
            </div>
          </div>
          {showSimple ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
        </button>

        {showSimple && (
          <div className="px-5 pb-5 space-y-4">
            <div className="p-4 rounded-xl bg-[#0B1121] border border-[#00CCC2]/20 space-y-3 text-sm">
              <p className="font-semibold text-[#00CCC2]">Format de la cle</p>
              <code className="block text-[#00CCC2] text-xs bg-[#131B2F] p-3 rounded-lg">
                PH-{"{T}{NNN}"}-XXXX-XXXX-XXXX
              </code>
              <div className="space-y-2 text-slate-400">
                <p><strong className="text-white">T</strong> = Type de licence : <code>S</code>=Standard, <code>P</code>=Premium, <code>E</code>=Enterprise</p>
                <p><strong className="text-white">NNN</strong> = Nombre max d'utilisateurs (zero-padded, ex: 005)</p>
                <p><strong className="text-white">XXXX-XXXX-XXXX</strong> = Hash unique (non cryptographique)</p>
                <p className="text-amber-400 mt-2">Ce format ne contient pas de signature. Validez via lookup en base ou API SCIAD.</p>
              </div>
            </div>

            <div className="flex gap-2 border-b border-white/10 pb-2">
              <button onClick={() => setActiveTab("python")} className={`px-4 py-2 text-sm rounded-lg transition-colors ${activeTab === "python" ? "bg-[#00CCC2]/20 text-[#00CCC2]" : "text-slate-400 hover:bg-white/5"}`}>Python</button>
              <button onClick={() => setActiveTab("javascript")} className={`px-4 py-2 text-sm rounded-lg transition-colors ${activeTab === "javascript" ? "bg-[#00CCC2]/20 text-[#00CCC2]" : "text-slate-400 hover:bg-white/5"}`}>JavaScript</button>
            </div>

            <CodeBlock code={simpleCode[activeTab] || simpleCode.python} lang={`simple-${activeTab}`} />
          </div>
        )}
      </div>

      {/* Quick reference */}
      <div className="p-5 rounded-2xl bg-[#131B2F] border border-white/10 text-sm">
        <p className="font-semibold mb-3">Resume rapide</p>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/10 text-slate-400">
                <th className="py-2 pr-4">Generateur</th>
                <th className="py-2 pr-4">Algorithme</th>
                <th className="py-2 pr-4">Format</th>
                <th className="py-2">Securite</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-white/10">
                <td className="py-2 pr-4 font-medium text-[#2395E3]">ClaimFlow</td>
                <td className="py-2 pr-4">HMAC-SHA256</td>
                <td className="py-2 pr-4"><code>CF-payload.signature</code></td>
                <td className="py-2 text-green-400">Cryptographique</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-2 pr-4 font-medium text-[#00CCC2]">ProjetHub</td>
                <td className="py-2 pr-4">Simple hash</td>
                <td className="py-2 pr-4"><code>PH-S005-XXXX-XXXX-XXXX</code></td>
                <td className="py-2 text-amber-400">Lecture seule</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-medium text-purple-400">Generic</td>
                <td className="py-2 pr-4">HMAC-SHA256</td>
                <td className="py-2 pr-4"><code>XX-payload.signature</code></td>
                <td className="py-2 text-green-400">Cryptographique</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LicenseIntegrationGuide;
